import React, { useEffect, useState } from 'react';
import { listMetaLeadAssets } from '../api/adpilotApi';

/** Page-scoped Instant Form and CRM pixel selection for conversion leads. */
export default function MetaLeadSetupFields({ connectionId, pages = [], value, onChange }) {
  const pageId = value.metaLeadPageId || '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ key: '', status: 'idle', catalog: null });
  const key = `${connectionId || ''}:${pageId}`;
  useEffect(() => {
    let current = true;
    if (!connectionId || !pageId) {
      setState({ key, status: 'idle', catalog: null });
      return () => { current = false; };
    }
    setState({ key, status: 'loading', catalog: null });
    listMetaLeadAssets({ connectionId, pageId }).then(catalog => {
      if (!current) return;
      if (String(catalog?.page?.id) !== pageId || !Array.isArray(catalog.forms) || !Array.isArray(catalog.pixels)) {
        setState({ key, status: 'error', catalog: null });
        return;
      }
      setState({ key, status: 'ready', catalog });
    }).catch(() => {
      if (current) setState({ key, status: 'error', catalog: null });
    });
    return () => { current = false; };
  }, [connectionId, pageId, key, attempt]);
  // Do not render one account/Page's assets while another request is in flight.
  const active = state.key === key ? state : { status: 'loading', catalog: null };
  const catalog = active.catalog;
  const ready = active.status === 'ready';
  const selectedForm = catalog?.forms?.find(form => form.id === value.metaLeadFormId);
  const selectedPixel = catalog?.pixels?.find(pixel => pixel.id === value.facebookPixelId);
  const change = patch => onChange({ ...value, ...patch, crmIntegrationReady: false });
  return <section className="campaign-setup" aria-label="Conversion leads setup">
    <h3>Instant Form and CRM setup</h3>
    <p>Conversion leads optimizes Instant Form ads using lead-stage feedback from your CRM. Choose the existing Page, form and CRM pixel you intend to use.</p>
    <label className="builder-field"><span>Facebook Page</span><select required value={pageId}
      onChange={event => change({ metaLeadPageId: event.target.value, metaLeadFormId: '' })}>
      <option value="">Choose a Page</option>
      {pages.map(page => <option key={page.page_id} value={page.page_id}>{page.name}</option>)}
    </select></label>
    <p>Lead-ad calls to action: Apply now, Download, Get quote, Learn more, Sign up or Subscribe.</p>
    {!pages.length && <p>No Pages are available. Check Page access on the connected Meta account.</p>}
    {pageId && active.status === 'loading' && <p role="status">Loading Instant Forms and CRM pixels…</p>}
    {pageId && active.status === 'error' && <div role="alert"><p>Could not load lead form setup. Check Page access and lead-ad permissions, then reconnect Meta if needed.</p>
      <button type="button" className="btn btn-secondary" onClick={() => setAttempt(count => count + 1)}>Retry lead setup</button></div>}
    {ready && <>
      {!catalog.page?.lead_ads_terms_accepted && <p role="alert">Meta has not confirmed Lead Ads Terms acceptance for this Page. Accept the terms in Meta before creating lead ads.</p>}
      <label className="builder-field"><span>Instant Form</span><select required value={selectedForm?.id || ''}
        onChange={event => change({ metaLeadFormId: event.target.value })}>
        <option value="">Choose an active form</option>
        {(catalog.forms || []).map(form => <option key={form.id} value={form.id}>{form.name} · {form.id}</option>)}
      </select></label>
      {!catalog.forms?.length && <p>No active Instant Forms are available on this Page. Create or reactivate the intended form in Meta, then refresh.</p>}
      <label className="builder-field"><span>CRM pixel or dataset</span><select required value={selectedPixel?.id || ''}
        onChange={event => change({ facebookPixelId: event.target.value })}>
        <option value="">Choose the pixel receiving CRM events</option>
        {(catalog.pixels || []).map(pixel => <option key={pixel.id} value={pixel.id}>{pixel.name} · {pixel.id}</option>)}
      </select></label>
      {!catalog.pixels?.length && <p>No pixels are available to this ad account. Share the intended CRM pixel with the account in Meta, then refresh.</p>}
      <p>Access to a pixel does not prove CRM events are arriving or that your sales funnel is configured.</p>
      {value.metaLeadFormId && !selectedForm && <p role="alert">The saved form is no longer available on this Page. Choose an active form.</p>}
      {value.facebookPixelId && !selectedPixel && <p role="alert">The saved CRM pixel is no longer available. Choose an available pixel.</p>}
      <label><input type="checkbox" required checked={Boolean(value.crmIntegrationReady)}
        disabled={!selectedForm || !selectedPixel || !catalog.page?.lead_ads_terms_accepted}
        onChange={event => onChange({ ...value, crmIntegrationReady: event.target.checked })} /> My CRM sends lead-stage events to this pixel, and I have configured the conversion-leads sales funnel in Meta.</label>
      <button type="button" className="btn btn-secondary" onClick={() => { change({}); setAttempt(count => count + 1); }}>Refresh lead setup</button>
    </>}
  </section>;
}
